import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { Calculator, ArrowRight, Check } from "lucide-react";

// Average nightly rates (EUR) for the Portuguese short-term rental market — 2026 estimates
const BASE_RATES = { t0: 70, t1: 90, t2: 130, t3: 190, t4: 260 };

// Zone multipliers — Greater Lisbon
const ZONE_MULT = {
  principe_real: 1.30,
  baixa_chiado: 1.35,
  alfama: 1.25,
  belem: 1.15,
  parque_nacoes: 1.10,
  estrela_lapa: 1.20,
  campo_ourique: 1.05,
  alvalade: 0.95,
  cascais: 1.30,
  sintra: 1.10,
  almada: 0.90,
  outra: 0.85,
};

// Amenity multipliers (multiplicative)
const AMENITY_MULT = {
  sea_view: 1.15,
  pool: 1.12,
  ac: 1.05,
  parking: 1.05,
  balcony: 1.08,
};

// AL status — impacts confidence in reaching the estimate
const AL_MULT = {
  self: 1.0,
  other_company: 1.0,
  none: 1.0,
};

const MANAGEMENT_FEE = 0.18;
const OWN_INEFFICIENCY = 0.78; // solo owners typically capture ~78% of the potential rate

const RevenueCalculator = () => {
  const { t, lang } = useLang();
  const [type, setType] = useState("t2");
  const [location, setLocation] = useState("principe_real");
  const [amenities, setAmenities] = useState([]);
  const [alStatus, setAlStatus] = useState("self");
  const [nights, setNights] = useState(18);

  const currency = useMemo(
    () =>
      new Intl.NumberFormat(lang === "pt" ? "pt-PT" : "en-US", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }),
    [lang]
  );

  const toggleAmenity = (value) =>
    setAmenities((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  const { monthlyOwn, monthlyNet, yearlyNet, rate } = useMemo(() => {
    const base = BASE_RATES[type] || 130;
    const zone = ZONE_MULT[location] || 1;
    const amenityBoost = amenities.reduce((acc, a) => acc * (AMENITY_MULT[a] || 1), 1);
    const alFactor = AL_MULT[alStatus] || 1;
    const nightlyStayzen = Math.round(base * zone * amenityBoost * alFactor);
    const nightlyOwn = Math.round(nightlyStayzen * OWN_INEFFICIENCY);

    const safeNights = Math.min(Math.max(Number(nights) || 0, 0), 30);
    const monthlyGrossStayzen = nightlyStayzen * safeNights;
    const monthlyGrossOwn = nightlyOwn * safeNights;

    const monthlyNetStayzen = Math.round(monthlyGrossStayzen * (1 - MANAGEMENT_FEE));
    return {
      rate: nightlyStayzen,
      monthlyOwn: Math.round(monthlyGrossOwn),
      monthlyNet: monthlyNetStayzen,
      yearlyNet: monthlyNetStayzen * 12,
    };
  }, [type, location, amenities, alStatus, nights]);

  const fieldSelect =
    "w-full bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-3 text-[#0A1128] cursor-pointer text-sm";

  return (
    <section
      id="calculator"
      className="border-y border-[#0A1128]/10 bg-[#F4F1EB]"
      data-testid="calculator-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline mb-6 flex items-center gap-2">
            <Calculator size={14} strokeWidth={1.4} /> {t.calculator.overline}
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight text-[#0A1128]">
            {t.calculator.title}
          </h2>
          <p className="mt-6 text-[#3A435E] leading-relaxed font-light">
            {t.calculator.subtitle}
          </p>
          <p className="mt-10 text-xs text-[#3A435E]/70 font-light italic max-w-sm">
            {t.calculator.disclaimer}
          </p>
        </div>

        <div
          className="md:col-span-7 bg-[#FCFAF8] border border-[#0A1128]/10 p-8 md:p-12"
          data-testid="calculator-card"
        >
          {/* Row 1 · Type + Zone */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-3">{t.calculator.type_label}</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={fieldSelect}
                data-testid="calc-type"
              >
                {t.calculator.type_options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-3">{t.calculator.location_label}</label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className={fieldSelect}
                data-testid="calc-location"
              >
                {t.calculator.location_options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2 · Amenities */}
          <div className="mt-8">
            <label className="overline block mb-3">{t.calculator.amenities_label}</label>
            <div className="flex flex-wrap gap-2" data-testid="calc-amenities">
              {t.calculator.amenities_options.map((o) => {
                const active = amenities.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggleAmenity(o.value)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 text-xs border transition-all ${
                      active
                        ? "bg-[#0A1128] border-[#0A1128] text-[#F4F1EB]"
                        : "bg-transparent border-[#0A1128]/20 text-[#3A435E] hover:border-[#0A1128] hover:text-[#0A1128]"
                    }`}
                    data-testid={`calc-amenity-${o.value}`}
                    aria-pressed={active}
                  >
                    <span
                      className={`w-3.5 h-3.5 border grid place-items-center ${
                        active ? "border-[#C5A059] bg-[#C5A059]" : "border-[#0A1128]/30"
                      }`}
                    >
                      {active && <Check size={9} strokeWidth={3} className="text-[#0A1128]" />}
                    </span>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Row 3 · AL status */}
          <div className="mt-8">
            <label className="overline block mb-3">{t.calculator.al_label}</label>
            <select
              value={alStatus}
              onChange={(e) => setAlStatus(e.target.value)}
              className={fieldSelect}
              data-testid="calc-al-status"
            >
              {t.calculator.al_options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Row 4 · Nights slider */}
          <div className="mt-8">
            <div className="flex items-end justify-between mb-3">
              <label className="overline">{t.calculator.nights_label}</label>
              <span className="font-serif-display text-2xl text-[#0A1128]" data-testid="calc-nights-value">
                {nights}
                <span className="text-[#C5A059]">/</span>
                <span className="text-[#3A435E]/60 text-base">30</span>
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={30}
              value={nights}
              onChange={(e) => setNights(Number(e.target.value))}
              className="w-full accent-[#C5A059] cursor-pointer"
              data-testid="calc-nights"
            />
            <div className="flex justify-between mt-2 text-xs text-[#3A435E]/60">
              <span>0</span>
              <span>15</span>
              <span>30</span>
            </div>
          </div>

          {/* Comparison result */}
          <div className="mt-10 border-t border-[#0A1128]/10 pt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                className="border border-[#0A1128]/10 p-6 bg-transparent"
                data-testid="calc-own-card"
              >
                <div className="text-xs text-[#3A435E]/80">{t.calculator.compare_own}</div>
                <div className="font-serif-display text-4xl md:text-5xl font-light text-[#0A1128] mt-3">
                  {currency.format(monthlyOwn)}
                </div>
                <div className="text-xs text-[#3A435E]/70 mt-2">{t.calculator.compare_own_hint}</div>
              </div>
              <div
                className="border border-[#C5A059]/50 bg-[#C5A059]/10 p-6 relative"
                data-testid="calc-stayzen-card"
              >
                <span className="absolute top-3 right-3 text-[9px] tracking-[0.24em] uppercase text-[#C5A059]">
                  Recomendado
                </span>
                <div className="text-xs text-[#0A1128]/90">{t.calculator.compare_stayzen}</div>
                <div
                  className="font-serif-display text-4xl md:text-5xl font-light text-[#0A1128] mt-3"
                  data-testid="calc-result-monthly"
                >
                  {currency.format(monthlyNet)}
                </div>
                <div className="text-xs text-[#0A1128]/80 mt-2">{t.calculator.compare_stayzen_hint}</div>
              </div>
            </div>
            <div className="flex items-baseline justify-between mt-4 text-xs text-[#3A435E]/70">
              <span data-testid="calc-result-yearly">
                {t.calculator.yearly}: <span className="text-[#C5A059]">{currency.format(yearlyNet)}</span>
              </span>
              <span>~{currency.format(rate)}/noite · {t.calculator.fee_note}</span>
            </div>
          </div>

          {/* Disclaimer + CTA compact block (below results) */}
          <div className="mt-8 border-t border-[#0A1128]/10 pt-8">
            <p className="text-xs text-[#3A435E]/80 leading-relaxed">{t.calculator.disclaimer}</p>
            <Link
              to={lang === "pt" ? "/marcar-reuniao" : "/book-meeting"}
              className="mt-5 w-full inline-flex items-center justify-center gap-3 bg-[#0A1128] text-white hover:bg-[#162243] transition-colors duration-300 px-7 py-4 text-xs tracking-[0.22em] uppercase group"
              data-testid="calc-cta"
            >
              {t.calculator.cta}
              <ArrowRight
                size={14}
                strokeWidth={1.5}
                className="transition-transform duration-500 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RevenueCalculator;
